/* SAP cart-drawer plan-change controller.
 *
 * Wires the cart-drawer controls that change a line between one-time and
 * subscription (and between cadences):
 *   - .sap-cd-upgrade-cta  — "Upgrade to Subscription" button on one-time lines
 *   - .sap-cd-plan-select  — cadence <select> on subscription lines (also offers
 *                            "One-time purchase" to downgrade)
 *
 * Before this controller these controls had markup + CSS + a transition pill but
 * NO JavaScript at all — clicking did nothing (no listener, no /cart/change.js).
 *
 * Re-render safety: the drawer re-renders its line items via Aurora's
 * 'update-nodes' mechanism on every cart change, replacing these elements. So we
 * use EVENT DELEGATION on document — the listeners survive every re-render.
 *
 * Refresh path (identical to assets/sap-pdp-cadence-box.js): request the
 * cart-drawer section back with the change via the Section Rendering API
 * (sections + sections_url on /cart/change.js) and hand the rendered section to
 * the drawer's own purchaseHandler — it emits 'update-nodes' so every cart
 * sub-block re-renders and the line swaps to its new state. The freshly-rendered
 * Liquid replaces the transition pill, so the "Upgrading…" state ends naturally.
 *
 * Line identifier: /cart/change.js needs a 1-based `line`. The controls carry a
 * 0-based index (data-line-index / data-line), so line = index + 1. Using the
 * line position (not the variant id) disambiguates two lines of the same product
 * — e.g. a one-time AND a subscription "Day" in the same cart.
 */
(function () {
  "use strict";

  /* Cart-wide in-flight lock: /cart/change.js line numbers are positional, so a
     second change fired while one is pending can land on a renumbered line after
     Shopify merges/removes lines — mutating the WRONG line. One change at a time. */
  var inFlight = false;

  function drawerAndSection() {
    var drawer = document.querySelector("cart-drawer");
    var sectionId = (drawer && drawer.dataset.sectionId) || "cart-drawer";
    return { drawer: drawer, sectionId: sectionId };
  }

  function setTransition(planRow, label) {
    if (!planRow) return;
    var labelEl = planRow.querySelector(".sap-cd-plan-transition__label");
    if (labelEl) labelEl.textContent = label;
    planRow.classList.add("is-transitioning");
  }

  function clearTransition(planRow) {
    if (planRow) planRow.classList.remove("is-transitioning");
  }

  /* POST a line change to /cart/change.js, then refresh the drawer natively.
     opts: { line, quantity, sellingPlan (string|''), planRow, label,
             control (the button/select — recovered on error/no-render),
             revertValue (select value to restore on failure) } */
  function changeLine(opts) {
    var ds = drawerAndSection();
    inFlight = true;
    setTransition(opts.planRow, opts.label);

    var body = new FormData();
    body.append("line", opts.line);
    if (opts.quantity) body.append("quantity", opts.quantity);
    /* selling_plan: a plan id changes/upgrades the line; '' removes it (revert to
       one-time). Always sent so the change is explicit. */
    body.append("selling_plan", opts.sellingPlan == null ? "" : opts.sellingPlan);
    body.append("sections", ds.sectionId);
    body.append("sections_url", window.location.pathname);

    return fetch("/cart/change.js", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/javascript",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: body,
    })
      .then(function (res) {
        return res.text().then(function (text) {
          var json = null;
          try {
            json = JSON.parse(text);
          } catch (_) {}
          if (!res.ok) {
            throw new Error((json && (json.description || json.message)) || text.slice(0, 200) || "HTTP " + res.status);
          }
          return json;
        });
      })
      .then(function (json) {
        var sectionHTML = json && json.sections && json.sections[ds.sectionId];
        if (ds.drawer && typeof ds.drawer.purchaseHandler === "function" && sectionHTML) {
          /* purchaseHandler expects a PARSED Document (Aurora calls parseHTML
             first). It emits 'update-nodes' so the items re-render with the new
             line state, then opens the drawer (idempotent — already open here). */
          var parsed = new DOMParser().parseFromString(sectionHTML, "text/html");
          ds.drawer.purchaseHandler(parsed, json);
        } else if (!ds.drawer) {
          /* No <cart-drawer> on this page — the /cart page excludes the drawer
             markup, so nothing client-side can re-render the cart. The change IS
             applied server-side; reload so prices/plan state show the truth
             instead of a silently mutated cart behind a stale page. */
          window.location.reload();
          return;
        } else {
          /* Drawer present but no section HTML (e.g. dev proxy stripped it):
             clear the transition and recover the control. The change is already
             applied server-side and will show on the next drawer render. */
          clearTransition(opts.planRow);
          if (opts.control) {
            opts.control.disabled = false;
            if (opts.control.tagName === "SELECT") opts.control.dataset.lastValue = opts.control.value;
          }
        }
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      })
      .catch(function (err) {
        console.error("[sap-cart-plan-change]", err);
        /* Recover the control — otherwise the button stays permanently disabled
           and the select keeps displaying a cadence the server never applied. */
        if (opts.control) {
          opts.control.disabled = false;
          if (opts.revertValue != null && opts.control.tagName === "SELECT") {
            opts.control.value = opts.revertValue;
          }
        }
        setTransition(opts.planRow, "Couldn’t update — try again");
        setTimeout(function () {
          clearTransition(opts.planRow);
        }, 2500);
      })
      .finally(function () {
        inFlight = false;
      });
  }

  /* ── Upgrade button (one-time → subscription) ── */
  document.addEventListener("click", function (e) {
    var btn = e.target.closest(".sap-cd-upgrade-cta");
    if (!btn || btn.disabled || inFlight) return;
    e.preventDefault();
    btn.disabled = true;
    changeLine({
      line: parseInt(btn.dataset.lineIndex, 10) + 1,
      quantity: btn.dataset.quantity,
      sellingPlan: btn.dataset.sellingPlanId,
      planRow: btn.closest(".cart-item__plan-row"),
      label: "Upgrading…",
      control: btn,
    });
  });

  /* ── Cadence select (change cadence, or "One-time purchase" → downgrade) ── */
  document.addEventListener("change", function (e) {
    var sel = e.target.closest(".sap-cd-plan-select");
    if (!sel) return;
    if (inFlight) {
      /* Another change is pending — accepting this one would race it with a
         stale line index. Snap back to the last server-confirmed cadence. */
      sel.value = sel.dataset.lastValue || "";
      return;
    }
    sel.disabled = true;
    changeLine({
      line: parseInt(sel.dataset.line, 10) + 1,
      quantity: sel.dataset.quantity,
      sellingPlan: sel.value, // '' = one-time, else a selling_plan id
      planRow: sel.closest(".cart-item__plan-row"),
      label: "Updating…",
      control: sel,
      revertValue: sel.dataset.lastValue || "",
    });
  });
})();
